import moment from 'moment';

export const formatTicketDate = (created) => {
  if (!created) return 'N/A';
  return moment(created, 'x').format('DD/MM/YY LT');
};

export const formatTicketChannel = (channel, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  const channelMap = {
    w: t('sales.channel_website', { _: 'Website' }),
    m: t('sales.channel_mobile', { _: 'Mobile' }),
    sp: t('sales.channel_sale_point', { _: 'Sale Point' }),
    sa: t('sales.channel_sale_agent', { _: 'Sale Agent' }),
    op: t('sales.channel_org_promoter', { _: 'Org C. Promoter' }),
    vp: t('sales.channel_venue_promoter', { _: 'Venue Promoter' }),
    a: t('sales.channel_affiliate', { _: 'Affiliate' }),
    ocm: t('sales.channel_org_manager', { _: 'Org C. Manager' }),
    vm: t('sales.channel_venue_manager', { _: 'Venue Manager' }),
  };
  return channelMap[channel] || 'N/A';
};

export const formatUserFullName = (user) => {
  if (!user || !user.name) return 'N/A';
  return `${user.name} ${user.surname || ''}`.trim();
};

export const formatEventName = (event) => {
  if (!event) return 'N/A';
  return event.name || 'N/A';
};

export const formatProductInfo = (ticket) => {
  if (ticket.seats && ticket.seats.length > 0) {
    const seats = ticket.seats.map((seat) => seat.id);
    return seats.join(', ');
  } else if (ticket.product && ticket.product.title && ticket.product.title.en) {
    return ticket.product.title.en;
  }
  return 'N/A';
};

export const formatProductKey = (ticket) => {
  if (ticket.isInvitation) return 'Invitation';
  if (ticket.isReservation) return 'Reservation';

  if (ticket.product && ticket.product.key) {
    return ticket.product.key;
  }
  // Yeni finalize bazen yalnızca product.id yazar; panelle uyum için fallback.
  if (ticket.product && ticket.product.id) {
    return ticket.product.id;
  }
  if (ticket.seats) {
    return ticket.seats.map((seat) => seat.selectedOption?.title?.en || seat.id).join(', ');
  }
  return 'N/A';
};

export const formatTicketUsage = (ticket, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  return ticket.isUsed
    ? t('tickets.usage_yes', { _: 'Evet' })
    : t('tickets.usage_no', { _: 'Hayır' });
};

export const isTicketRefunded = (ticket) => {
  return Boolean(ticket?.extras?.refunded);
};

export const hasTicketRefundRequest = (ticket) => {
  return Boolean(
    ticket?.extras?.refundRequested ||
      ticket?.extras?.refundPending ||
      ticket?.extras?.refundRequestId,
  );
};

export const formatTicketRefund = (ticket, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  if (isTicketRefunded(ticket)) return t('tickets.refunded', { _: 'İade edildi' });
  if (hasTicketRefundRequest(ticket)) return t('tickets.refund_pending', { _: 'İade bekliyor' });
  return '-';
};

/** Ticket'tan iade bilgisi sayfasi URL'si (hash router path). */
export const resolveTicketRefundPath = (ticket) => {
  if (!ticket) return null;

  const paymentDirect =
    typeof ticket.payment === 'string' ? ticket.payment : ticket.payment?.id || null;
  if (paymentDirect) {
    return `/payments/${encodeURIComponent(paymentDirect)}/show`;
  }

  const salePayment =
    ticket.sale && typeof ticket.sale === 'object'
      ? typeof ticket.sale.payment === 'string'
        ? ticket.sale.payment
        : ticket.sale.payment?.id
      : null;
  if (salePayment) {
    return `/payments/${encodeURIComponent(salePayment)}/show`;
  }

  const refundRequestId = ticket.extras?.refundRequestId;
  if (refundRequestId) {
    return `/refundRequests?highlight=${encodeURIComponent(refundRequestId)}`;
  }

  return null;
};

export const getTicketSaleId = (ticket) => {
  if (!ticket?.sale) return null;
  return typeof ticket.sale === 'string' ? ticket.sale : ticket.sale?.id || null;
};

export const getTicketUsageColor = (isUsed) => {
  return isUsed ? 'green' : 'red';
};

export const getTicketUsageText = (isUsed, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  return isUsed ? t('tickets.usage_yes', { _: 'Evet' }) : t('tickets.usage_no', { _: 'Hayır' });
};

export const checkTicketsPermissions = (permissions) => {
  let isManager = false;
  let canView = false;

  if (permissions?.crole) {
    if (permissions.crole === 'organisationCompanyManager' && permissions.rid) {
      isManager = true;
      canView = true;
    } else if (permissions.crole === 'venueManager' && permissions.rid) {
      isManager = true;
      canView = true;
    } else if (permissions.crole === 'admin') {
      canView = true;
    }
  }

  return {
    isManager,
    canView,
  };
};

export const getTicketsPageTitle = (eventID, stockID, translate) => {
  const t = translate || ((k, opts) => opts?._ || k);
  if (eventID && stockID) {
    return {
      title: t('tickets.title_session', { _: 'Event Session / Tickets List' }),
      description: t('tickets.desc_session', {
        _: 'This list shows all tickets for the selected event session.',
      }),
    };
  } else if (eventID) {
    return {
      title: t('tickets.title_event', { _: 'Event / Tickets List' }),
      description: t('tickets.desc_event', {
        _: 'This list shows all tickets for the selected event.',
      }),
    };
  } else {
    return {
      title: t('tickets.title_default', { _: 'Tickets List' }),
      description: t('tickets.desc_default', {
        _: 'This list shows the last 100 entries ordered on date/time, in order to view more and specific tickets you can query them via events page.',
      }),
    };
  }
};

export const getManagerColumns = (columns) => {
  return columns.filter((column) => column.field !== 'channel');
};

export const checkForSeatOverlap = (tickets) => {
  return tickets.some((ticket) => ticket.seats && ticket.seats.length > 0);
};

export const getTicketPrintUrls = (pnr) => {
  return {
    ticket: `https://gisekibris.com/etkinlik-bileti/${pnr}`,
    print8cm: `https://gisekibris.com/bilet-yazdir/${pnr}`,
  };
};
